<template>
  <div>
    <h4>Recherche GCR</h4>
    <div v-if="!isConcession" class="d-flex align-items-center mb-2" style="gap:8px; flex-wrap:nowrap">
      <b-form-radio-group
        v-model="searchMode"
        :options="[{ text: 'Recherche libre', value: 'text' }, { text: 'Groupe', value: 'group' }]"
        buttons
        button-variant="outline-secondary"
        size="sm"
        style="flex-shrink:0; white-space:nowrap"
        @change="onSearchModeChange"
      />

      <div v-if="searchMode === 'text'" class="d-flex align-items-center" style="gap:8px; flex:1 1 auto; min-width:0">
        <b-input
          v-model="input_search"
          placeholder="Mail, alcor (ex: N0001) ou numéro d'affaire"
          @keyup.enter="search"
          style="flex:1 1 auto; min-width:150px; max-width:400px"
        />
        <b-button variant="primary" @click="search" :disabled="loading" style="flex-shrink:0">
          <b-spinner v-if="loading" small />
          <i v-else class="fas fa-search mr-1"></i>
          Rechercher
        </b-button>
      </div>

      <div v-else class="position-relative d-flex align-items-center" style="gap:8px; flex:1 1 auto; min-width:0; max-width:400px">
        <b-input
          v-model="groupQuery"
          placeholder="Rechercher un groupe..."
          autocomplete="off"
          style="flex:1 1 auto; min-width:0"
          @focus="showGroupDropdown = true"
          @blur="showGroupDropdown = false"
        />
        <b-spinner v-if="groupsLoading || loading" small />
        <div
          v-if="showGroupDropdown && filteredGroups.length"
          class="list-group position-absolute"
          style="top:100%; left:0; z-index:1000; max-height:250px; overflow-y:auto; width:100%"
        >
          <button
            v-for="g in filteredGroups"
            :key="g.group_uid"
            type="button"
            class="list-group-item list-group-item-action"
            @mousedown.prevent="selectGroup(g)"
          >
            {{ g.label }}
            <span class="text-muted small ml-1">({{ g.nb_affaires }} affaire{{ g.nb_affaires > 1 ? 's' : '' }})</span>
          </button>
        </div>
      </div>
    </div>
    <p v-if="isConcession" class="text-muted small mb-3">
      Vous ne pouvez consulter que vos propres informations, ainsi que les alcors/numéros d'affaire de votre groupe.
    </p>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <!-- Contact(s) trouvé(s) — recherche par mail / auto (self) -->
    <div v-if="uniqueContacts.length" class="card mb-3">
      <div class="card-header"><strong>Contact{{ uniqueContacts.length > 1 ? 's' : '' }}</strong></div>
      <div class="card-body">
        <div v-for="c in uniqueContacts" :key="c.mail1" class="mb-2">
          <strong>{{ c.prenom }} {{ c.nom }}</strong>
          <br />
          <small class="text-muted">{{ c.mail1 }}</small>
          <span v-if="c.mobile"> · {{ c.mobile }}</span>
          <div v-if="c.roles.length">
            <small v-for="(role, i) in c.roles" :key="i" class="text-muted">{{ role }}<span v-if="i < c.roles.length - 1"> / </span></small>
          </div>
        </div>
        <div class="mt-2">
          <strong>Alcors accessibles :</strong> {{ myAlcors.join(', ') || '—' }}
        </div>
      </div>
    </div>

    <!-- Contacts ayant accès — recherche par alcor / num_affaire / groupe -->
    <div v-if="searchType === 'alcor' || searchType === 'num_affaire' || searchType === 'group'" class="card mb-3">
      <div class="card-header"><strong>Contacts ayant accès</strong></div>
      <div v-if="alcorContacts.length" class="card-body pt-3 px-2 pb-0">
        <edit_tabulator ref="tabulator_contacts" :extern_file="false" />
      </div>
      <div v-else class="card-body text-muted">
        Aucun contact associé à cet alcor.
      </div>
    </div>

    <!-- Affaires directement accessibles / de l'alcor recherché -->
    <div v-if="myAffaires.length" class="card mb-3">
      <div class="card-header">
        <strong>{{ affairesCardTitle }}</strong>
      </div>
      <div class="card-body pt-3 px-2 pb-0">
        <edit_tabulator ref="tabulator_my_affaires" :extern_file="false" />
      </div>
    </div>

    <!-- Autres affaires du groupe (non accessibles directement) -->
    <div v-if="groupAffaires.length" class="card mb-3">
      <div class="card-header">
        <strong>{{ searchType === 'mail' ? 'Autres affaires du groupe (non accessibles directement)' : 'Autres affaires du groupe' }}</strong>
      </div>
      <div class="card-body pt-3 px-2 pb-0">
        <edit_tabulator ref="tabulator_group_affaires" :extern_file="false" />
      </div>
    </div>

    <!-- Formations de la campagne -->
    <div v-if="formationStats.length" class="mb-3">
      <h5>Formations — campagne {{ campagneLabel }}</h5>
      <div v-for="stat in formationStats" :key="stat.alcor" class="card mb-2">
        <div
          class="card-header d-flex align-items-center justify-content-between"
          style="cursor:pointer"
          @click="toggleStat(stat.alcor)"
        >
          <div>
            <strong>{{ stat.affaire_nom }}</strong>
            <span class="text-muted ml-2" style="font-size:0.85em">{{ stat.alcor }}</span>
            <span v-if="!stat.accessible" class="badge badge-secondary ml-2">Groupe</span>
          </div>
          <div class="d-flex align-items-center" style="gap:8px">
            <span class="badge badge-success">{{ stat.completes }} complété{{ stat.completes > 1 ? 's' : '' }}</span>
            <span v-if="stat.attente > 0" class="badge badge-warning">{{ stat.attente }} en attente</span>
            <i :class="openStats[stat.alcor] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
          </div>
        </div>
        <div v-if="openStats[stat.alcor]" class="card-body pt-3 px-2 pb-0">
          <edit_tabulator :ref="'tab_form_' + stat.alcor" :extern_file="false" />
        </div>
      </div>
    </div>

    <div v-if="!loading && searched && !formationStats.length && !myAffaires.length" class="text-muted">
      Aucun résultat trouvé.
    </div>
  </div>
</template>

<script>
// Pas d'import ES ici (ex: "import store from ...") : ce fichier peut être compilé au
// runtime via new Function(...) par l'éditeur de composants (vueComponent.vue ::
// compileComponent()), qui n'accepte aucune syntaxe import/export dans le corps du script.
// this.user est injecté automatiquement par dashboard/vueComponent.vue :: extendComponentOptions()
// (this.user = utilisateur simulé "tester en tant que" si présent, sinon store.state.user) —
// AUCUN import requis, et surtout : ne JAMAIS utiliser this.$store.state.user directement ici,
// qui resterait toujours le vrai utilisateur connecté même pendant un test "tester en tant que",
// faussant l'affichage (barre de recherche visible à tort pour un admin testant un concessionnaire).

// Identifiant du groupe "concession" (cf saas_front/src/components/vueComponent/gcrSearchScript.js,
// exécuté côté back — c'est LÀ qu'est faite la vérification qui compte, non falsifiable).
// Ici, ce même identifiant sert UNIQUEMENT à décider côté front si l'appel silencieux
// d'auto-recherche du profil (au montage de la page) a un sens à être déclenché : ce n'est pas
// un identifiant secret (déjà visible ailleurs côté front), et aucune donnée sensible n'en dépend.
const CONCESSION_GROUP_UID = '43622bde-8da7-46de-9037-19d3c2f56f89';

function isConcessionUser(user) {
  if (!user || !user.groups) return false;
  const groups = Array.isArray(user.groups) ? user.groups : String(user.groups).split('|').filter(Boolean);
  return groups.indexOf(CONCESSION_GROUP_UID) !== -1;
}

export default {
  props: ['format', 'data', 'client'],
  data() {
    return {
      input_search: '',
      searchMode: 'text', // 'text' (recherche libre) ou 'group' (autocomplete par groupe)
      groupQuery: '',
      groupsList: [], // [{ group_uid, label, nb_affaires }], chargée une fois via list_groups
      groupsLoading: false,
      groupsLoaded: false,
      showGroupDropdown: false,
      loading: false,
      searched: false,
      error: null,
      searchType: null,
      // Valeur par défaut ; recalculée dans created() une fois this.user disponible (injecté par
      // le wrapper vueComponent.vue, cf commentaire en tête de fichier). Le back refait de toute
      // façon sa propre vérification (autoritaire) dès qu'une recherche est lancée.
      isConcession: false,

      // Recherche par mail
      contacts: [],

      // Recherche par alcor / num_affaire
      alcorContacts: [],

      // Commun
      myAffaires: [],
      groupAffaires: [],
      formationStats: [],
      openStats: {}
    };
  },

  computed: {
    filteredGroups() {
      const q = (this.groupQuery || '').toLowerCase().trim();
      if (!q) return this.groupsList;
      return this.groupsList.filter(g => (g.label || '').toLowerCase().includes(q));
    },
    affairesCardTitle() {
      if (this.searchType === 'mail') return 'Affaires accessibles';
      if (this.searchType === 'group') return 'Affaires du groupe';
      return 'Affaire(s)';
    },
    uniqueContacts() {
      const byMail = {};
      for (const c of this.contacts) {
        const key = c.mail1 || '';
        if (!byMail[key]) {
          byMail[key] = { ...c, roles: [] };
        }
        const role = [c.fonction, c.domaine_activite].filter(Boolean).join(' · ');
        if (role && !byMail[key].roles.includes(role)) {
          byMail[key].roles.push(role);
        }
      }
      return Object.values(byMail);
    },
    myAlcors() {
      return [...new Set(this.contacts.map(c => c.alcor_concession).filter(Boolean))];
    },
    campagneLabel() {
      const v = this.client && this.client.var ? this.client.var : {};
      if (v.campagne_nom) return v.campagne_nom;
      return v.campagne_date || '(non définie)';
    }
  },

  methods: {
    reset() {
      this.contacts = [];
      this.myAffaires = [];
      this.groupAffaires = [];
      this.alcorContacts = [];
      this.formationStats = [];
      this.openStats = {};
      this.error = null;
      this.searched = false;
    },

    // Bascule texte libre / groupe. Le back refuse ces deux modes pour un utilisateur concession
    // (is_concession force input/group_uid/list_groups à vide côté script, cf gcrSearchScript.js) :
    // le v-if="!isConcession" sur le bloc parent empêche déjà d'afficher ce toggle pour eux.
    async onSearchModeChange(mode) {
      this.showGroupDropdown = false;
      if (mode === 'group') {
        await this.ensureGroupsLoaded();
      }
    },

    // Charge la liste des groupes (une seule fois, mise en cache) pour l'autocomplete. Le back
    // fait un GROUP BY sur affaires.group (cf doListGroups() dans gcrSearchScript.js).
    async ensureGroupsLoaded() {
      if (this.groupsLoaded || this.groupsLoading) return;
      this.groupsLoading = true;
      try {
        const response = await this.code.run_by_name("recherche", { list_groups: true });
        if (!response.errors) {
          this.groupsList = (response.result && response.result.groups) || [];
          this.groupsLoaded = true;
        } else {
          console.error('GCR list_groups error:', response.errors);
        }
      } catch (err) {
        console.error('GCR list_groups error:', err);
      }
      this.groupsLoading = false;
    },

    selectGroup(g) {
      this.groupQuery = g.label;
      this.showGroupDropdown = false;
      this.runSearch({ group_uid: g.group_uid });
    },

    async toggleStat(alcor) {
      this.$set(this.openStats, alcor, !this.openStats[alcor]);
      if (this.openStats[alcor]) {
        await this.$nextTick();
        const ref = this.$refs['tab_form_' + alcor];
        const t = Array.isArray(ref) ? ref[0] : ref;
        const stat = this.formationStats.find(s => s.alcor === alcor);
        if (t && stat) t.set_data(stat.formations, ['titre', 'identifiant_salarie_2', 'debut', 'fin', 'duree', 'montant', 'statut_form']);
      }
    },

    async updateTabulators() {
      // this.alcorContacts / this.myAffaires / this.groupAffaires arrivent déjà enrichis
      // (champ "contacts") depuis le script exécuté côté back (GCR_SEARCH_SCRIPT).
      if (this.$refs.tabulator_contacts && this.alcorContacts.length)
        this.$refs.tabulator_contacts.set_data(this.alcorContacts, ['nom', 'prenom', 'mail1', 'alcor_concession', 'fonction', 'domaine_activite']);
      if (this.$refs.tabulator_my_affaires && this.myAffaires.length)
        this.$refs.tabulator_my_affaires.set_data(this.myAffaires, ['affaire', 'num_affaire', 'code_affaire', 'alcor', 'group', 'contacts']);
      if (this.$refs.tabulator_group_affaires && this.groupAffaires.length)
        this.$refs.tabulator_group_affaires.set_data(this.groupAffaires, ['affaire', 'num_affaire', 'code_affaire', 'alcor', 'group', 'contacts']);
    },

    // Convertit yyyy-mm-dd → dd/mm/yyyy
    campagneDateFormatted() {
      const v = this.client && this.client.var ? this.client.var : {};
      const d = v.campagne_date;
      if (!d) return null;
      return d.split('-').reverse().join('/');
    },

    // Appel générique du script serveur (GCR_SEARCH_SCRIPT). `payload` contient éventuellement
    // `input_search` (absent/vide = recherche de son propre profil, côté back). N'est appelée
    // que lorsqu'une recherche est réellement demandée (bouton, entrée clavier, ou montage de
    // page pour un utilisateur concession, cf mounted()) — jamais d'office pour les autres.
    async runSearch(payload) {
      this.loading = true;
      this.reset();

      try {
        const response = await this.code.run_by_name("recherche", Object.assign({
          campagne_date: this.campagneDateFormatted()
        }, payload));

        if (response.errors) {
          this.error = 'Erreur lors de la recherche : ' + response.errors;
        } else {
          const result = response.result || {};
          this.searchType = result.searchType || null;
          this.error = result.error || null;
          this.contacts = result.contacts || [];
          this.alcorContacts = result.alcorContacts || [];
          this.myAffaires = result.myAffaires || [];
          this.groupAffaires = result.groupAffaires || [];
          this.formationStats = result.formationStats || [];
          // isConcession n'est PLUS réassigné ici : c'est un état purement front, dérivé une
          // fois pour toutes de this.user dans created() (cf isConcessionUser ci-dessus). Le
          // réassigner depuis result.is_concession écrasait la valeur affichée après chaque
          // recherche et cassait l'affichage (barre de recherche qui réapparaît à tort).
        }
      } catch (err) {
        console.error('GCR search error:', err);
        this.error = 'Erreur lors de la recherche : ' + (err.message || err);
      }

      this.loading = false;
      this.searched = true;
      await this.$nextTick();
      await this.updateTabulators();
    },

    async search() {
      const input = this.input_search.trim();
      if (!input) return;
      await this.runSearch({ input_search: input });
    }
  },

  // Appelé APRÈS que le wrapper vueComponent.vue ait assigné this.user (réel ou simulé "tester
  // en tant que", cf commentaire en tête de fichier) mais AVANT mounted() : c'est le bon
  // endroit pour recalculer isConcession, this.user n'étant pas encore disponible au moment
  // où data() s'exécute.
  created() {
    this.isConcession = isConcessionUser(this.user);
  },

  async mounted() {
    // Auto-recherche de son propre profil au chargement : UNIQUEMENT pour les utilisateurs du
    // groupe concession (déterminé localement via this.user, cf isConcessionUser ci-dessus) — les
    // autres utilisateurs n'ont aucune raison de déclencher une recherche avant d'avoir
    // explicitement cliqué sur un bouton.
    if (this.isConcession) {
      await this.runSearch({});
    }
  }
};
</script>

<style scoped>
.card-header {
  padding: 10px 15px;
}
.badge {
  font-size: 0.8em;
}
</style>
